import React, {useRef, useState} from 'react';

import {Button, TextField} from '@betareader/design-system';
import Icon from '../../../../components/AppIcon';
import styled from 'styled-components';

// i18n
import {withTranslation} from 'react-i18next';

import usePostProcessingContext from './postProcessingContext';
import textUtil from '../../../../utils/textUtil';

const HorizontalWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  gap: 8px;
  max-width: 340px;
`;

// `text.split(' ').length - 1` undercounted every block by one word — a
// single-word paragraph scored 0 — which fed straight into the empty-part
// filter below. Count non-whitespace runs instead, matching how the API counts.
const countBlockWords = block => (block.innerText.match(/\S+/g) || []).length;

const ChapterParser = ({t}) => {
  const {originalDocumentData, setDocumentData, resetDocumentData} =
    usePostProcessingContext();
  const [chapterSeparator, setChapterSeparator] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const timeoutRef = useRef(null);
  const onCancelParse = () => {
    setChapterSeparator('');
    resetDocumentData();
  };
  const updateChapterSeparator = event => {
    const newChapterSeparator = event.target.value;
    setChapterSeparator(newChapterSeparator);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (newChapterSeparator.length === 0) {
      setDocumentData({
        ...originalDocumentData
      });
      return;
    }

    // update chapter separation
    timeoutRef.current = setTimeout(() => {
      try {
        setIsProcessing(true);

        // merge all chapters into one
        let doc = '';
        for (let i = 0; i < originalDocumentData.parts.length; i += 1) {
          doc += originalDocumentData.parts[i].html;
        }
        // find all new parts
        let newParts = [];
        // NB: `el` is a detached element (never attached to the document), used
        // only to walk the manuscript's block structure for chapter splitting.
        // Browsers do not execute scripts or fire resource/event handlers
        // (onerror/onload) for disconnected nodes, so this is not an XSS sink.
        // The content is later rendered through sanitizeHtml at the actual sinks.
        const el = document.createElement('body');
        el.innerHTML = doc;
        const blocks = el.children;
        for (let b = 0; b < blocks.length; b += 1) {
          const block = blocks[b];
          const content = block.innerText;
          if (
            newChapterSeparator === '{CAPS}' &&
            textUtil.isCapitalized(content)
          ) {
            // found new part based on all caps check
            newParts.push({
              title: block.innerText,
              html: '',
              wordCount: 0,
              characterCount: 0
            });
          } else if (content.startsWith(newChapterSeparator)) {
            // found new part start
            newParts.push({
              title: block.innerText,
              html: '',
              wordCount: 0,
              characterCount: 0
            });
          } else if (b === 0) {
            // if we didn't get a separator match, but are at the first block, create a part
            newParts.push({
              title: undefined,
              html: block.outerHTML,
              // the first block's own words used to be left uncounted, so a
              // leading part of a single paragraph scored 0 and was then
              // dropped by the filter below
              wordCount: countBlockWords(block),
              characterCount: block.innerText.length
            });
          } else {
            // continuing on an existing part
            const currentPart = newParts.pop() ?? {};
            currentPart.html += block.outerHTML;
            currentPart.wordCount += countBlockWords(block);
            currentPart.characterCount += block.innerText.length;
            newParts.push(currentPart);
          }
        }
        // Remove parts that carry nothing at all.
        //
        // This used to be `p.wordCount !== 0`, which equated "has no words"
        // with "is empty" — so a chapter whose body is a full-page
        // illustration (an <img> contributes no text) was deleted outright,
        // chapter and image together. Word count alone cannot decide this;
        // the part is empty only if it has no markup either.
        newParts = newParts.filter(
          p => p.wordCount !== 0 || (p.html && p.html.trim())
        );
        setDocumentData({
          ...originalDocumentData,
          parts: [...newParts]
        });
      } catch (error) {
        console.log('failed to find occurences', error);
      } finally {
        setIsProcessing(false);
      }
    }, 1000);
  };
  return (
    <>
      <HorizontalWrapper>
        <TextField
          id="chapter-separator"
          label={t('ChapterSeparator')}
          type="text"
          value={chapterSeparator}
          onChange={updateChapterSeparator}
        />
        <Button variant="secondary" aria-label={t('Cancel')} onClick={onCancelParse}>
          <Icon name="close" />
        </Button>
      </HorizontalWrapper>
      {isProcessing && <span role="status">{t('Processing')}</span>}
    </>
  );
};

export default withTranslation()(ChapterParser);
